import { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, PencilIcon, PlusIcon, ShieldCheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  adminListUsers, rechtegruppeAnlegen, rechtegruppeLoeschen, rechtegruppeNutzerSetzen, rechtegruppeOrdnerSetzen,
  rechtegruppeUmbenennen, rechtegruppenListe, type AuthUser, type Rechtegruppe,
} from '../../api/client';
import { useDialog } from '../../contexts/ConfirmContext';
import OrdnerBaum from './OrdnerBaum';

type Flash = (msg: string, variant?: 'ok' | 'err') => void;

const gleich = (a: string[], b: string[]) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);
const ordnerName = (p: string) => (p === '' ? 'Ganzes Laufwerk' : p);

function GruppeZeile({ gruppe, nutzerListe, startOffen, flash, onGeaendert, onGeloescht }: {
  gruppe: Rechtegruppe;
  nutzerListe: AuthUser[];
  startOffen: boolean;
  flash: Flash;
  onGeaendert: (g: Rechtegruppe) => void;
  onGeloescht: (id: string) => void;
}) {
  const { confirm, prompt } = useDialog();
  const bekannt = useMemo(() => new Set(nutzerListe.map((u) => u.id)), [nutzerListe]);
  const basisNutzer = useMemo(() => gruppe.nutzer.filter((id) => bekannt.has(id)), [gruppe.nutzer, bekannt]);

  const [offen, setOffen] = useState(startOffen);
  const [baumOffen, setBaumOffen] = useState(false);
  const [ordner, setOrdner] = useState<string[]>(gruppe.ordner);
  const [nutzer, setNutzer] = useState<string[]>(basisNutzer);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  // Nach dem Speichern/Neuladen kommt die Gruppe aktualisiert zurück → Entwurf angleichen.
  const ordnerKey = gruppe.ordner.join('\n');
  const nutzerKey = basisNutzer.join('\n');
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { setOrdner(gruppe.ordner); }, [ordnerKey]);
  useEffect(() => { setNutzer(basisNutzer); }, [nutzerKey]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const ordnerGeaendert = !gleich(ordner, gruppe.ordner);
  const nutzerGeaendert = !gleich(nutzer, basisNutzer);
  const geaendert = ordnerGeaendert || nutzerGeaendert;

  const toggleOrdner = (p: string) => setOrdner((cur) => (
    cur.includes(p) ? cur.filter((x) => x !== p) : [...cur.filter((x) => x === p || !x.startsWith(p)), p]
  ));
  const toggleNutzer = (id: string) => setNutzer((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const speichern = async () => {
    setBusy(true);
    try {
      let g = gruppe;
      if (ordnerGeaendert) g = await rechtegruppeOrdnerSetzen(gruppe.id, ordner);
      if (nutzerGeaendert) g = await rechtegruppeNutzerSetzen(gruppe.id, nutzer);
      onGeaendert(g);
      flash(`Rechtegruppe «${g.name}» gespeichert.`);
    } catch (e: any) { flash(e?.message ?? String(e), 'err'); }
    finally { setBusy(false); }
  };

  const umbenennen = async () => {
    const neu = await prompt({ title: 'Rechtegruppe umbenennen', message: 'Neuer Name der Gruppe:', defaultValue: gruppe.name, okLabel: 'Umbenennen' });
    const name = neu?.trim();
    if (!name || name === gruppe.name) return;
    try { onGeaendert(await rechtegruppeUmbenennen(gruppe.id, name)); flash('Gruppe umbenannt.'); }
    catch (e: any) { flash(e?.message ?? String(e), 'err'); }
  };

  const loeschen = async () => {
    if (!await confirm({
      title: 'Rechtegruppe löschen?',
      message: `«${gruppe.name}» wird gelöscht. Die zugeordneten Nutzer verlieren dadurch den Zugriff auf die Ordner dieser Gruppe, sofern sie nicht über eine andere Gruppe Rechte haben.`,
      okLabel: 'Löschen', danger: true,
    })) return;
    try { await rechtegruppeLoeschen(gruppe.id); onGeloescht(gruppe.id); flash(`Rechtegruppe «${gruppe.name}» gelöscht.`); }
    catch (e: any) { flash(e?.message ?? String(e), 'err'); }
  };

  const sichtbar = nutzerListe.filter((u) => {
    const f = filter.trim().toLowerCase();
    return !f || [u.name, u.username, u.email].some((x) => (x ?? '').toLowerCase().includes(f));
  });

  return (
    <li className="rounded-lg border border-ps-dark-200">
      <div className="flex items-center gap-1 pr-2">
        <button type="button" onClick={() => setOffen(!offen)} aria-expanded={offen}
          className="flex min-w-0 flex-1 items-center gap-2 px-3 py-3 text-left">
          {offen ? <ChevronDownIcon className="h-4 w-4 shrink-0 text-ps-dark-400" /> : <ChevronRightIcon className="h-4 w-4 shrink-0 text-ps-dark-400" />}
          <span className="min-w-0">
            <span className="block break-words font-medium text-ps-dark-900">{gruppe.name}</span>
            <span className="block text-xs text-ps-dark-500">
              <span className={gruppe.ordner.length === 0 ? 'text-amber-800' : ''}>{gruppe.ordner.length} Ordner</span>
              {' · '}
              <span className={basisNutzer.length === 0 ? 'text-amber-800' : ''}>{basisNutzer.length} Nutzer</span>
            </span>
          </span>
        </button>
        <button type="button" onClick={() => void umbenennen()} title="Umbenennen" aria-label="Gruppe umbenennen"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ps-dark-400 hover:bg-ps-dark-100 hover:text-ps-dark-700"><PencilIcon className="h-4 w-4" /></button>
        <button type="button" onClick={() => void loeschen()} title="Löschen" aria-label="Gruppe löschen"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ps-dark-400 hover:bg-rose-50 hover:text-rose-700"><TrashIcon className="h-4 w-4" /></button>
      </div>

      {offen && (
        <div className="space-y-5 border-t border-ps-dark-100 px-3 py-4">
          <section>
            <h3 className="text-sm font-semibold text-ps-dark-800">Ordner</h3>
            <p className="mb-2 text-xs text-ps-dark-400">Auf diese Ordner (samt allem darunter) dürfen die Nutzer der Gruppe zugreifen.</p>
            {ordner.length === 0 ? <div className="mb-2 text-xs text-amber-800">Noch kein Ordner gewählt — die Gruppe gibt nichts frei.</div> : (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {[...ordner].sort().map((p) => (
                  <span key={p} className="badge badge-brand max-w-full gap-1 break-all">
                    {ordnerName(p)}
                    <button type="button" onClick={() => toggleOrdner(p)} aria-label={`${ordnerName(p)} entfernen`} className="shrink-0 hover:text-rose-700"><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <button type="button" onClick={() => setBaumOffen(!baumOffen)} className="btn-outline mb-2 py-1.5 text-sm">
              {baumOffen ? 'Ordnerauswahl schliessen' : 'Ordner auswählen'}
            </button>
            {baumOffen && <OrdnerBaum ausgewaehlt={ordner} onToggle={toggleOrdner} />}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-ps-dark-800">Nutzer</h3>
            <p className="mb-2 text-xs text-ps-dark-400">Diese Personen sehen die Dateien der gewählten Ordner.</p>
            {nutzerListe.length > 8 && (
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Person suchen …" aria-label="Person suchen" className="input-field mb-2" />
            )}
            <ul className="max-h-72 divide-y divide-ps-dark-100 overflow-y-auto rounded-lg border border-ps-dark-200">
              {sichtbar.map((u) => (
                <li key={u.id}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50">
                    <input type="checkbox" checked={nutzer.includes(u.id)} onChange={() => toggleNutzer(u.id)}
                      className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-500 focus:ring-primary-500" />
                    <span className={`min-w-0 flex-1 ${u.active ? '' : 'opacity-50'}`}>
                      <span className="block break-words text-sm text-ps-dark-900">{u.name || u.username || u.email}</span>
                      <span className="block break-all text-[11px] text-ps-dark-400">{u.email}</span>
                    </span>
                    {u.role === 'admin' && <span className="badge badge-neutral shrink-0" title="Administratoren sehen ohnehin alle Dateien">Administrator</span>}
                    {!u.active && <span className="badge badge-neutral shrink-0">deaktiviert</span>}
                  </label>
                </li>
              ))}
              {sichtbar.length === 0 && <li className="px-3 py-4 text-center text-xs text-ps-dark-400">Niemand gefunden.</li>}
            </ul>
          </section>

          <div className="flex items-center justify-end gap-3">
            {geaendert && <span className="text-xs text-amber-800">Nicht gespeicherte Änderungen</span>}
            <button type="button" onClick={() => void speichern()} disabled={busy || !geaendert} className="btn-primary text-sm">{busy ? 'Speichere …' : 'Speichern'}</button>
          </div>
        </div>
      )}
    </li>
  );
}

/** Karte „Rechtegruppen": welche Nutzer welche Ordner sehen. */
export default function RechtegruppenKarte({ flash }: { flash: Flash }) {
  const [gruppen, setGruppen] = useState<Rechtegruppe[] | null>(null);
  const [nutzer, setNutzer] = useState<AuthUser[]>([]);
  const [neuerName, setNeuerName] = useState('');
  const [neuId, setNeuId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let weg = false;
    Promise.all([rechtegruppenListe(), adminListUsers()])
      .then(([g, u]) => { if (!weg) { setGruppen(g); setNutzer(u); } })
      .catch((e: any) => { if (!weg) { setGruppen([]); flash(e?.message ?? String(e), 'err'); } });
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = neuerName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const g = await rechtegruppeAnlegen(name);
      setGruppen((cur) => [...(cur ?? []), g].sort((a, b) => a.name.localeCompare(b.name, 'de-CH')));
      setNeuId(g.id); setNeuerName('');
      flash(`Rechtegruppe «${g.name}» angelegt. Wählen Sie jetzt Ordner und Nutzer.`);
    } catch (err: any) { flash(err?.message ?? String(err), 'err'); }
    finally { setBusy(false); }
  };

  const ersetzen = (g: Rechtegruppe) => setGruppen((cur) => (cur ?? []).map((x) => (x.id === g.id ? g : x)).sort((a, b) => a.name.localeCompare(b.name, 'de-CH')));

  return (
    <div className="card space-y-4">
      <div>
        <h2 className="page-h1 flex items-center gap-2"><ShieldCheckIcon className="h-5 w-5 text-ps-dark-400" /> Rechtegruppen</h2>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ps-dark-400">
          Eine Rechtegruppe verbindet Ordner mit Personen. Administratoren sehen immer alle Dateien. Nutzer ohne Rechtegruppe sehen nichts.
        </p>
      </div>

      {gruppen === null ? (
        <div className="space-y-2"><div className="shimmer h-3 w-1/3 rounded" /><div className="shimmer h-3 w-2/3 rounded" /></div>
      ) : (
        <>
          {gruppen.length === 0 ? (
            <div className="rounded-lg border border-dashed border-ps-dark-200 px-4 py-6 text-center text-sm text-ps-dark-500">
              Noch keine Rechtegruppe. Solange es keine gibt, sehen nur Administratoren Dateien.
            </div>
          ) : (
            <ul className="space-y-3">
              {gruppen.map((g) => (
                <GruppeZeile key={g.id} gruppe={g} nutzerListe={nutzer} startOffen={g.id === neuId} flash={flash}
                  onGeaendert={ersetzen} onGeloescht={(id) => setGruppen((cur) => (cur ?? []).filter((x) => x.id !== id))} />
              ))}
            </ul>
          )}
          <form onSubmit={anlegen} className="flex gap-2 border-t border-ps-dark-100 pt-4">
            <input value={neuerName} onChange={(e) => setNeuerName(e.target.value)} placeholder="Name der neuen Gruppe, z. B. Buchhaltung" aria-label="Name der neuen Rechtegruppe"
              maxLength={120} className="input-field min-w-0 flex-1" />
            <button type="submit" disabled={busy || !neuerName.trim()} className="btn-primary flex shrink-0 items-center gap-1 text-sm"><PlusIcon className="h-4 w-4" /> Anlegen</button>
          </form>
        </>
      )}
    </div>
  );
}
